import type { ParsedQuery } from 'query-string'

import { isNotNull } from './collectionTools'

export type QueryParamRaw = ParsedQuery<string>[string]

const separator = (url: string): string => (url.includes('?') ? '&' : '?')

export const addQueryParamsToUrl = (url: string) => (params: string[]) =>
  params.length > 0 ? `${url}${separator(url)}${params.join('&')}` : url

export const createQueryParam = (key: string, value: string | null): string | null =>
  value ? `${key}=${encodeURIComponent(value)}` : null

export const addFragmentToUrl = (url: string, fragment: string): string => `${url}#${fragment}`

export const excludeNullParamValues = (param: QueryParamRaw): string | string[] | undefined => {
  if (!Array.isArray(param)) {
    return param ?? undefined
  }
  // optimize assuming that the array does not normally contain any `null` values
  if (param.every(isNotNull)) {
    return param as string[]
  }
  return param.filter(isNotNull)
}

export const coerceParamToStringArray = (param: ParsedQuery<string>[string]): string[] => {
  const withoutNulls = excludeNullParamValues(param)
  return withoutNulls == null ? [] : Array.isArray(withoutNulls) ? withoutNulls : [withoutNulls]
}

export const parseStringParam = <T extends string = string>(
  paramValue: QueryParamRaw,
  defaultValue?: T,
  validator: (value: T) => boolean = (value) => !!value,
): T | undefined => {
  if (typeof paramValue !== 'string') {
    return defaultValue
  }
  const value = paramValue.trim() as T
  if (!validator(value)) {
    return defaultValue
  }
  return value
}

export const parseNumberParam = (
  paramValue: QueryParamRaw,
  defaultValue?: number,
  validator: (value: number) => boolean = (value) => !isNaN(value),
): number | undefined => {
  const number = Number(paramValue)
  if (!validator(number)) {
    return defaultValue
  }
  return number
}
